import { getAccessToken, isMsalEnabled } from "@/auth/msal";
import { observeUnauthorized, observeServerPrincipal } from "@/auth/session";
import { locale } from "@/shared/i18n";
import { MAINTENANCE_HEADER, reloadOntoMaintenancePage } from "./maintenance";

const API_BASE = "/api";

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  /**
   * Allow automatic retries for this call. Defaults to true for GET (idempotent — covers the
   * API-cold-start 502 case the retry loop was written for) and false for everything else:
   * retrying a POST that already reached the server duplicates its side effect (a placed
   * order, a sent quotation email, a created account).
   */
  retry?: boolean;
  /**
   * What the endpoint returns. "json" (the default) is everything here bar one: the admin email
   * preview renders a template and answers with the HTML itself, which has to come back as text.
   * Handled here rather than by a separate fetch so it still carries the bearer, the sales token
   * and the language header — a hand-rolled fetch for that one route silently lost all three.
   */
  responseType?: "json" | "text" | "blob";
  /**
   * Called with the response headers before the body is read. One caller: the email preview, whose body
   * is the email itself, so what the server actually used (session data or a stand-in) has nowhere to
   * travel but a header.
   */
  onHeaders?: (headers: Headers) => void;
  /**
   * How long one attempt may take before it is abandoned, in milliseconds. Thirty seconds unless the
   * endpoint says otherwise: placing an internal order writes a whole configuration tree to D365 and
   * takes minutes, rendering a document or sending a batch of mail takes a good many seconds. A stalled
   * connection — a phone changing networks, an App Service asleep — must not leave the caller loading
   * for ever, with a GET retrying three times on top.
   */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 30_000;

/** Key under which the sales deep-link token is held for the browser session. */
export const SALES_TOKEN_KEY = "lob-sales-token";

export async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {} } = options;
  // A Blob or FormData goes on the wire as it is and names its own content type; everything else is JSON.
  const raw = body instanceof Blob || body instanceof FormData;
  const retries = (options.retry ?? method === "GET") ? 3 : 0;

  // Resend the sales deep-link token (if any) so the server resolves the Internal
  // session context for every call, not just the initial resolve-token.
  const salesToken =
    typeof sessionStorage !== "undefined" ? sessionStorage.getItem(SALES_TOKEN_KEY) : null;

  // Azure AD B2C access token, when the SPA is configured for it (production). Null in dev / when
  // MSAL is disabled — the request then relies on the server DevAuth bypass or the sales token.
  const bearer = await getAccessToken();

  const config: RequestInit = {
    method,
    headers: {
      ...(raw ? {} : { "Content-Type": "application/json" }),
      // The app's SELECTED UI language, so server-rendered output (quotation email + PDF) follows the
      // in-app toggle rather than the browser locale. Accept-Language can't be set from fetch (forbidden
      // header), hence a custom one; the backend's ResolveLanguage prefers it.
      "X-UI-Language": locale(),
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      ...(salesToken ? { "X-Sales-Token": salesToken } : {}),
      ...headers,
    },
    credentials: "include",
  };

  if (body !== undefined && body !== null) {
    config.body = raw ? (body as BodyInit) : JSON.stringify(body);
  }

  let lastError: Error | null = null;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const deadline = new AbortController();
    const timer = setTimeout(() => deadline.abort(), timeoutMs);
    try {
      const response = await fetch(`${API_BASE}${endpoint}`, { ...config, signal: deadline.signal });

      // Retry on 502 (API still starting)
      if (response.status === 502 && attempt < retries) {
        console.warn(`[API] ${method} ${endpoint} → 502, retrying in ${(attempt + 1) * 2}s...`);
        await new Promise(r => setTimeout(r, (attempt + 1) * 2000));
        continue;
      }

      // A 401 for someone who WAS signed in means the sign-in has lapsed, and retrying cannot change
      // that: the caller's generic "try again" invites a retry that fails identically every time, which
      // is how an expired session on a tab left open reads as the server being broken.
      //
      // Gated on having been signed in, because a 401 does not mean the same thing to everyone. Several
      // endpoints require authentication yet are called from pages an anonymous visitor can reach — the
      // postal-code lookup on the summary step is one, and it has always answered 401 for them. Without
      // this guard, typing a postal code told a visitor who had never signed in that their session had
      // expired.
      if (response.status === 401 && isMsalEnabled()) {
        console.warn(`[API] 401 on ${method} ${endpoint}.`);
        observeUnauthorized();
      }

      // The maintenance page went up after this page was loaded: reload onto it rather than show the reader an
      // application that can no longer reach its server.
      if (response.status === 503 && response.headers?.get(MAINTENANCE_HEADER)) reloadOntoMaintenancePage();

      // The principal the server actually served this request as. A 401 only ever comes from an endpoint
      // that requires authentication; the configurator's own calls allow anonymous callers and answer 200
      // regardless, so a lapse there shows up not as a failure but as a silent downgrade to Basic — with
      // the dealer's net prices stripped from the response. This catches it on the next call rather than
      // at the next thing that happens to need a user.
      if (isMsalEnabled()) observeServerPrincipal(response.headers.get("X-Session-Principal"));

      if (!response.ok) {
        const text = await response.text();
        let errorMessage = response.statusText;
        let details: Record<string, unknown> | undefined;
        try {
          const json = JSON.parse(text);
          errorMessage = json.error ?? json.title ?? json.detail ?? text;
          if (json && typeof json === "object") details = json as Record<string, unknown>;
        } catch {
          errorMessage = text || response.statusText;
        }
        console.error(`[API ${response.status}] ${method} ${endpoint}:`, errorMessage);
        throw new ApiError(response.status, errorMessage, details);
      }

      options.onHeaders?.(response.headers);

      if (response.status === 204) return undefined as T;
      return (options.responseType === "text"
        ? await response.text()
        : options.responseType === "blob"
          ? await response.blob()
          : await response.json()) as T;
    } catch (e) {
      if (e instanceof ApiError) throw e;
      const timedOut = e instanceof DOMException && e.name === "AbortError";
      lastError = timedOut ? new ApiError(0, "timeout") : (e as Error);
      // Network error (API not up yet) or a deadline passed — retry, where retrying is safe
      if (attempt < retries) {
        console.warn(`[API] ${method} ${endpoint} → ${timedOut ? "timed out" : "network error"}, retrying in ${(attempt + 1) * 2}s...`);
        await new Promise(r => setTimeout(r, (attempt + 1) * 2000));
        continue;
      }
    } finally {
      clearTimeout(timer);
    }
  }

  throw lastError ?? new ApiError(0, "Request failed after retries");
}

/**
 * A non-2xx answer. `message` is the server's error code (`"conflict"`, `"id-format"`) when the body
 * carried one; `details` is the whole body, for the field name or limit that goes with the code.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ── Query string builder ────────────────────────────────────────────────────

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== "");
  if (entries.length === 0) return "";
  return "?" + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join("&");
}
